/* eslint-disable @typescript-eslint/dot-notation */
export * from 'core/shared/nfcAuthentication'

import { DatadogEncodingRun, EncodingValidationRequest, TmrTag } from 'core/api'
import { RfidReader } from 'core/shared'
import { OperationConfig } from 'core/shared/RemoteOperation'
import { __, T } from 'core/shared/i18n'
import {
  checkNfcCollision,
  EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE,
  getNfcAuthApiBaseUrl,
  normalizeTagResponse,
  parseFinalUid,
  prepareMutualAuthAntenna
} from 'core/shared/nfcAuthentication'
import { decToHex, putSpacesEveryByte } from 'core/shared/utils'
import { NfcAuth, ValidateWithProductInfoResponse } from '../api/NfcAuth'

export const readNfcAuthenticatedTagWithProductInfo = async (
  encodingValidation: EncodingValidationRequest,
  _tag: TmrTag,
  operationConfig: OperationConfig,
  antennaId: string,
  _datadogEncodingSession?: DatadogEncodingRun
) => {
  try {
    const apiBaseUrl = getNfcAuthApiBaseUrl(operationConfig)

    const res = await Promise.race([
      new Promise<boolean>((_, reject) => {
        setTimeout(() => {
          reject(new Error(__(T.error.authentication_timeout)))
        }, 10000)
      }),
      (async () => {
        await prepareMutualAuthAntenna(antennaId)

        const result = await nfcMutualAuthenticationWithProductInfo(_tag, antennaId, apiBaseUrl)
        // auth failed: without this the `false` would be cast to a response object, every statusCode
        // check would silently no-op and the tag would be reported as authenticated
        if (!result) {
          throw new Error(__(T.error.tag_authentication_error))
        }
        const _result = result
        encodingValidation.itemAttributes = _result
        if (['F005'].includes(_result.statusCode)) {
          _tag.uid = undefined
          throw new Error(__(T.misc.unknown_tag))
        }
        if (['T999'].includes(_result.statusCode)) {
          _tag.uid = undefined
          throw new Error(__(T.misc.error))
        }
        if (_result.verificationData?.uid) {
          _tag.uid = _result.verificationData.uid
        }
        return true
      })()
    ])
    return res
  } catch (error: any) {
    console.warn('readNfcAuthenticatedTagWithProductInfo error', error)
    if (error.message?.includes('Collision')) {
      await RfidReader.stopWsCustomCommands(false, antennaId)
      if (!encodingValidation.identifiers.some((id) => id.identifierType === 'NFC_TAG')) {
        encodingValidation.identifiers.push({
          code: _tag.uid,
          identifierType: 'NFC_TAG'
        })
      }
      encodingValidation.identifiers.forEach((element) => {
        if (element.identifierType === 'NFC_TAG') {
          element._status = 'NFC_COLLISION'
          element._error = __(T.custom.tooManyTags)
        }
      })
      return false
    }

    encodingValidation.identifiers.forEach((element) => {
      if (element.identifierType === 'NFC_TAG') {
        element._status = 'ERROR'
        element._error = error?.message ? error.message : error
      }
    })
    return false
  }
}

const nfcMutualAuthenticationWithProductInfo = async (tag: TmrTag, antennaId: string, apiBaseUrl: string) => {
  const tagId = tag.uid
  if (!tagId) return false

  const dateInMilliseconds = Date.now()
  const dateInHex = decToHex(dateInMilliseconds)
  const challenge = (BigInt(`0x${dateInHex}`) << BigInt(4)).toString(16).toUpperCase()

  const antenna = RfidReader.getAntennas().find((a) => a.id === antennaId)
  if (!antenna) {
    throw new Error('Antenna not found')
  }
  const readerType = antenna.reader.deviceType

  await RfidReader.startWsCustomCommands(antennaId)

  await checkNfcCollision(readerType)

  const step1Res = await RfidReader.sendWsCustomCommandForResponse({
    command: putSpacesEveryByte('02D816038000' + challenge),
    parameters: {
      expectedResponseLength: EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE[readerType].step1
    }
  })

  const step1ServerRes = await NfcAuth.challenge(
    normalizeTagResponse(readerType, step1Res.result),
    challenge,
    apiBaseUrl
  )

  if (!step1ServerRes) return false

  const connectionId = step1ServerRes?.data?.connectionId ?? ''
  const apdu = step1ServerRes?.data?.APDU ?? ''

  if (!connectionId || !apdu) {
    throw new Error('No connection id or apdu from the server')
  }

  const step2Res = await RfidReader.sendWsCustomCommandForResponse({
    command: putSpacesEveryByte('02D816039000' + apdu),
    parameters: {
      expectedResponseLength: EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE[readerType].step2
    }
  })

  if ((step2Res.result as string)?.toLowerCase().includes('error')) {
    throw new Error('NFC authentication error')
  }

  if (['010F', '0F01'].includes(step2Res.result)) return false

  const finalUidRes = await RfidReader.sendWsCustomCommandForResponse({
    command: putSpacesEveryByte('022B'),
    parameters: {
      expectedResponseLength: EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE[readerType].getUid
    }
  })

  if ((finalUidRes.result as string)?.toLowerCase().includes('collision')) {
    throw new Error('NFC collision detected')
  }

  const parsedFinalUid = parseFinalUid(readerType, finalUidRes.result)

  const res: ValidateWithProductInfoResponse = await NfcAuth.validateWithProductInfo(
    normalizeTagResponse(readerType, step2Res.result),
    parsedFinalUid,
    connectionId,
    apiBaseUrl
  )
  await RfidReader.stopWsCustomCommands()
  return res
}
